import React, { useCallback, useEffect, useRef, useState } from 'react';
import syncConnectQueryService from '../services/syncConnectQueryService';
import ConfirmModal from './shared/ConfirmModal';
import SyncConnectQueryLibrary, { LibrarySaveModal } from './SyncConnectQueryLibrary';
import AdminSyncConnectStatusQueries from './AdminSyncConnectStatusQueries';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';
import { columnsOf, labelOf, toCsv } from '../utils/syncConnectResult';

// ADMIN › PLATFORM › SYNCCONNECT — ad-hoc read-only SQL sent to a project's SyncConnect, and what came back.
//
// The hub cannot reach a property's on-premise OPERA database; SyncConnect runs inside the property's network.
// A query written here is queued for one project, collected by its SyncConnect on its next poll, run (after the
// prequery, which sets the resort pms_p answers for), and the rows posted back. Superadmin only; SELECT only —
// the server refuses anything else (/opt/utils/syncconnect-adhoc).
//
// Rows arrive as col1…colN in select-list order, with the real names alongside (utils/syncConnectResult.js).

const PAGE_SIZE = 50;
const REFRESH_MS = 3000;
const DEFAULT_PREQUERY = "begin pms_p.initialize('NA_REPORTS','NA_REPORTS','${RESORT}'); end;";
const PENDING = new Set(['QUEUED', 'DELIVERED']);

const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;

function StatusPill({ status }) {
    return <span className={`scq-status scq-status-${String(status || '').toLowerCase()}`}>{status}</span>;
}

function AdhocQueries() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [projectFilter, setProjectFilter] = useState('');
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);
    const [composing, setComposing] = useState(null);   // { syncProjectId, title, sql, prequery, sending, error }
    const [copied, setCopied] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);   // the row/detail being deleted
    // THE LIBRARY (V585): the palette's reload key, its categories (for the save dialog), and the query being saved.
    const [libraryKey, setLibraryKey] = useState(0);
    const [libraryCategories, setLibraryCategories] = useState([]);
    const [libraryDraft, setLibraryDraft] = useState(null);   // { id?, title, category, description, sql, prequery }
    const inFlight = useRef(false);

    const load = useCallback(async (silent = false) => {
        if (silent && inFlight.current) return;
        inFlight.current = true;
        if (!silent) setLoading(true);
        try {
            const res = await syncConnectQueryService.list({ page, limit: PAGE_SIZE, ...(projectFilter ? { syncProjectId: projectFilter } : {}) });
            const data = res.data || res;
            setRows(data.rows || []);
            setTotal(data.total || 0);
            setError(null);
        } catch (e) {
            setError(errText(e));
        } finally {
            inFlight.current = false;
            if (!silent) setLoading(false);
        }
    }, [page, projectFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        syncConnectQueryService.projects()
            .then(res => setProjects((res.data || res).rows || []))
            .catch(e => setError(errText(e)));
    }, []);

    // WHILE ANYTHING IS ON ITS WAY, the list and the open query refresh themselves — the answer arrives whenever
    // the property's SyncConnect next polls, which is seconds, not something to keep pressing Refresh for.
    const pending = rows.some(r => PENDING.has(r.status)) || (detail && PENDING.has(detail.status));
    useEffect(() => {
        if (!pending) return undefined;
        const id = setInterval(async () => {
            load(true);
            if (detail && PENDING.has(detail.status)) {
                try { setDetail((await syncConnectQueryService.get(detail.id)).data); } catch (e) { /* next tick */ }
            }
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [pending, load, detail]);

    const open = async (id) => {
        setDetail({ id, _loading: true });
        try {
            setDetail((await syncConnectQueryService.get(id)).data);
        } catch (e) {
            setDetail({ id, _error: errText(e) });
        }
    };

    const startNew = (from) => setComposing({
        syncProjectId: from ? String(from.sync_project_id) : (projectFilter || ''),
        title: from ? from.title || '' : '',
        sql: from ? from.sql_text || '' : '',
        prequery: from ? (from.prequery || 'NONE') : DEFAULT_PREQUERY,
        sending: false,
        error: null,
    });

    const send = async () => {
        setComposing(c => ({ ...c, sending: true, error: null }));
        try {
            const res = await syncConnectQueryService.submit({
                syncProjectId: composing.syncProjectId,
                title: composing.title,
                sql: composing.sql,
                prequery: composing.prequery,
            });
            const row = res.data || res;
            setComposing(null);
            setPage(1);
            await load();
            open(row.id);
        } catch (e) {
            setComposing(c => ({ ...c, sending: false, error: errText(e) }));
        }
    };

    const remove = async (q) => {
        setConfirmDelete(null);
        try {
            await syncConnectQueryService.remove(q.id);
            if (detail && detail.id === q.id) setDetail(null);
            await load();
        } catch (e) {
            setError(errText(e));
        }
    };

    const copyCsv = async () => {
        try {
            await navigator.clipboard.writeText(toCsv(detail.result));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) { /* clipboard unavailable */ }
    };

    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const cols = detail && detail.result ? columnsOf(detail.result) : [];

    return (
        <div className="acp-page">
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">SyncConnect queries</h1>
                    <p className="acp-subtitle">
                        Read-only SQL sent to a project&apos;s SyncConnect and run against the property&apos;s on-premise database.
                        SELECT only. The answer arrives when SyncConnect next polls.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="acp-btn acp-btn-secondary" onClick={() => load()} disabled={loading}>Refresh</button>
                    <button className="acp-btn acp-btn-primary" onClick={() => startNew(null)}>+ New query</button>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}

            <div className="scq-layout">
            <div className="acp-card scq-main">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">{total} quer{total === 1 ? 'y' : 'ies'}</span>
                    <div className="odq-filters">
                        <select className="odq-filter-select" value={projectFilter}
                                onChange={e => { setProjectFilter(e.target.value); setPage(1); }}>
                            <option value="">All projects</option>
                            {projects.map(p => (
                                <option key={p.syncid} value={p.syncid}>{p.syncid} · {p.projectname || p.hotelname}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">No queries sent yet.</div>
                    : (
                        <table className="acp-table">
                            <thead>
                                <tr>
                                    <th>#</th><th>Project</th><th>Title</th><th>Status</th><th>Rows</th>
                                    <th>Sent by</th><th>Sent</th><th>Answered</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id} className="odq-clickable" onClick={() => open(r.id)} title={r.sql_preview}>
                                        <td>{r.id}</td>
                                        <td>{r.sync_project_id} · {r.projectname || r.hotelname || ''}</td>
                                        <td>{r.title}</td>
                                        <td><StatusPill status={r.status} /></td>
                                        <td>{r.row_count == null ? '—' : `${r.row_count}${r.result_truncated === 'Y' ? '+' : ''}`}</td>
                                        <td>{r.submitted_by || '—'}</td>
                                        <td>{when(r.created_at)}</td>
                                        <td>{when(r.returned_at)}</td>
                                        <td>
                                            <button className="acp-btn acp-btn-danger acp-btn-sm"
                                                    onClick={e => { e.stopPropagation(); setConfirmDelete(r); }}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                {pages > 1 && (
                    <div className="scq-pager">
                        <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                        <span>Page {page} of {pages}</span>
                        <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
                    </div>
                )}
            </div>

            {/* A saved query opens as a new one, for the project chosen in the filter (or picked in the dialog). */}
            <SyncConnectQueryLibrary
                refreshKey={libraryKey}
                onCategories={setLibraryCategories}
                onUse={(item) => startNew({ sync_project_id: projectFilter || '', title: item.title, sql_text: item.sql_text, prequery: item.prequery || 'NONE' })}
                onEdit={(item) => setLibraryDraft({ id: item.id, title: item.title, category: item.category || '', description: item.description || '', sql: item.sql_text, prequery: item.prequery || '' })}
            />
            </div>

            {composing && (
                <div className="odq-modal-overlay" onClick={() => !composing.sending && setComposing(null)}>
                    <div className="odq-modal scq-compose" onClick={e => e.stopPropagation()}>
                        <div className="odq-modal-head">
                            <span>New SyncConnect query</span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setComposing(null)} disabled={composing.sending}>Close</button>
                        </div>
                        <div className="odq-modal-body scq-form">
                            <label>Sync project
                                <select value={composing.syncProjectId} onChange={e => setComposing(c => ({ ...c, syncProjectId: e.target.value }))}>
                                    <option value="">Choose a project…</option>
                                    {projects.map(p => (
                                        <option key={p.syncid} value={p.syncid}>
                                            {p.syncid} · {p.projectname || p.hotelname} ({p.projectstatus})
                                            {p.dequeue_last_contact ? ` — last polled ${when(p.dequeue_last_contact)}` : ' — never polled'}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>Title
                                <input value={composing.title} placeholder="What this query is for"
                                       onChange={e => setComposing(c => ({ ...c, title: e.target.value }))} />
                            </label>
                            <label>SQL (one SELECT)
                                <textarea className="scq-sql" rows={12} value={composing.sql} spellCheck={false}
                                          placeholder="SELECT resv_name_id, confirmation_no, resv_status FROM reservation_general_view WHERE resort = pms_p.resort AND ..."
                                          onChange={e => setComposing(c => ({ ...c, sql: e.target.value }))} />
                            </label>
                            <label>Prequery (run first — sets the resort OPERA&apos;s views answer for; NONE to skip)
                                <input className="scq-mono" value={composing.prequery} spellCheck={false}
                                       onChange={e => setComposing(c => ({ ...c, prequery: e.target.value }))} />
                            </label>
                            {composing.error && <div className="os-msg os-msg-error">{composing.error}</div>}
                            <div className="scq-actions">
                                <button className="acp-btn acp-btn-secondary" onClick={() => setComposing(null)} disabled={composing.sending}>Cancel</button>
                                <button className="acp-btn acp-btn-secondary" disabled={composing.sending || !composing.sql.trim()}
                                        onClick={() => setLibraryDraft({ title: composing.title, category: '', description: '', sql: composing.sql, prequery: composing.prequery })}>
                                    Save to library
                                </button>
                                <button className="acp-btn acp-btn-primary" onClick={send}
                                        disabled={composing.sending || !composing.syncProjectId || !composing.sql.trim()}>
                                    {composing.sending ? 'Sending…' : 'Send to SyncConnect'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {detail && (
                <div className="odq-modal-overlay" onClick={() => setDetail(null)}>
                    <div className="odq-modal scq-detail" onClick={e => e.stopPropagation()}>
                        <div className="odq-modal-head">
                            <span>Query #{detail.id}{detail.title ? ` — ${detail.title}` : ''}</span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setDetail(null)}>Close</button>
                        </div>
                        <div className="odq-modal-body">
                            {detail._loading ? <div className="acp-empty">Loading…</div>
                                : detail._error ? <div className="os-msg os-msg-error">{detail._error}</div>
                                : (
                                    <>
                                        <div className="scq-meta">
                                            <StatusPill status={detail.status} />
                                            <span>Project {detail.sync_project_id} · {detail.projectname || detail.hotelname}</span>
                                            <span>Sent {when(detail.created_at)} by {detail.submitted_by || '—'}</span>
                                            <span>Collected {when(detail.delivered_at)}</span>
                                            <span>Answered {when(detail.returned_at)}</span>
                                            {detail.queue_id && <span>Request {detail.queue_id}</span>}
                                            {detail.delivery === 'UPLOAD' && <span>Run by hand (no SyncConnect) — the answer is the uploaded file</span>}
                                        </div>
                                        {PENDING.has(detail.status) && (
                                            <div className="scq-wait">
                                                {detail.status === 'QUEUED'
                                                    ? 'Waiting for SyncConnect to collect it on its next poll…'
                                                    : 'Collected — waiting for SyncConnect to run it and post the rows back…'}
                                            </div>
                                        )}
                                        <h4>SQL</h4>
                                        <pre className="scq-pre">{detail.sql_text}</pre>
                                        <h4>Prequery</h4>
                                        <pre className="scq-pre">{detail.prequery || 'NONE'}</pre>
                                        {detail.error_message && (
                                            <>
                                                <h4>Error</h4>
                                                <pre className="scq-pre scq-error">{detail.error_message}</pre>
                                            </>
                                        )}
                                        {detail.result && (
                                            <>
                                                <h4>
                                                    Result — {detail.row_count} row{detail.row_count === 1 ? '' : 's'}
                                                    {detail.result_truncated === 'Y' && ` (only ${detail.result.rows.length} kept — the answer was too large to store whole)`}
                                                    <button className="acp-btn acp-btn-secondary acp-btn-sm scq-copy" onClick={copyCsv}>
                                                        {copied ? 'Copied' : 'Copy as CSV'}
                                                    </button>
                                                </h4>
                                                <div className="scq-result">
                                                    <table className="acp-table">
                                                        <thead><tr>{cols.map(c => <th key={c}>{labelOf(detail.result, c)}</th>)}</tr></thead>
                                                        <tbody>
                                                            {detail.result.rows.map((r, i) => (
                                                                <tr key={i}>{cols.map(c => <td key={c}>{r[c] == null ? '' : String(r[c])}</td>)}</tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        )}
                                        <div className="scq-actions">
                                            <button className="acp-btn acp-btn-secondary" onClick={() => { const d = detail; setDetail(null); startNew(d); }}>
                                                Send again / edit
                                            </button>
                                            <button className="acp-btn acp-btn-secondary"
                                                    onClick={() => setLibraryDraft({ title: detail.title || '', category: '', description: '', sql: detail.sql_text || '', prequery: detail.prequery || '' })}>
                                                Save to library
                                            </button>
                                            <button className="acp-btn acp-btn-danger" onClick={() => setConfirmDelete(detail)}>
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                        </div>
                    </div>
                </div>
            )}

            {libraryDraft && (
                <LibrarySaveModal
                    draft={libraryDraft}
                    categories={libraryCategories}
                    onClose={() => setLibraryDraft(null)}
                    onSaved={() => { setLibraryDraft(null); setLibraryKey(k => k + 1); }}
                />
            )}

            {confirmDelete && (
                <ConfirmModal
                    title={`Delete query #${confirmDelete.id}?`}
                    message={confirmDelete.status === 'QUEUED'
                        ? 'It has not been collected yet, so it is also taken off the queue and will not be sent. It disappears from this list; the record is kept as the audit.'
                        : 'It disappears from this list; the record of what was sent and returned is kept as the audit.'}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => remove(confirmDelete)}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}
        </div>
    );
}

// THE PAGE: the ad-hoc queries (above) and the Status Queries tab (V613). The tab is kept in the URL (?tab=status) so a
// link or a refresh lands on it.
const TABS = [
    { id: 'queries', label: 'Queries' },
    { id: 'status', label: 'Status Queries' },
];

export default function AdminSyncConnectQueries() {
    const initial = new URLSearchParams(window.location.search).get('tab') === 'status' ? 'status' : 'queries';
    const [tab, setTab] = useState(initial);
    const choose = (id) => {
        setTab(id);
        const url = new URL(window.location.href);
        if (id === 'queries') url.searchParams.delete('tab'); else url.searchParams.set('tab', id);
        window.history.replaceState(null, '', url.toString());
    };
    return (
        <div>
            <div className="scq-tabs">
                {TABS.map(t => (
                    <button key={t.id} type="button" className={`scq-tab ${tab === t.id ? 'active' : ''}`} onClick={() => choose(t.id)}>{t.label}</button>
                ))}
            </div>
            {tab === 'status'
                ? <div className="acp-page"><h1 className="acp-title">SyncConnect status queries</h1><AdminSyncConnectStatusQueries /></div>
                : <AdhocQueries />}
        </div>
    );
}
